import type { SearchDocument } from '@/features/AudioPlayer';
import type { Track } from '@/types/tracks';
import type { DocumentSchema } from '@music-vine/setlist';

export const DOCUMENT_ID_REGEX = /^(.+)_(\d+)_(free|premium)$/;

/**
 * Extracts the numeric ID from a formatted document ID like "sunny-beats_123_premium".
 * Falls back to parsing the full string as a number if no match is found.
 *
 * The `id` field is now excluded from tracksWithEngagement/sfxWithEngagement queries
 * (to avoid leaking slug-prefixed crawlable strings into the SSR payload), so callers
 * should prefer `track_id` / `sfx_variant_id` and treat this as a legacy fallback.
 */
export const extractNumericId = (documentId: string | undefined): number => {
  if (!documentId) {
    return 0;
  }
  const match = documentId.match(DOCUMENT_ID_REGEX);
  if (match) {
    return Number.parseInt(match[2], 10);
  }
  // Fallback for direct numeric IDs
  return Number.parseInt(documentId, 10);
};

/**
 * Extracts the slug from a formatted document ID like "sunny-beats_123_premium".
 * Returns empty string if no match is found or input is missing.
 */
export const extractSlugFromDocumentId = (
  documentId: string | undefined
): string => {
  if (!documentId) {
    return '';
  }
  const match = documentId.match(DOCUMENT_ID_REGEX);
  return match ? match[1] : '';
};

/**
 * Extracts the appropriate track ID from a SearchDocument based on asset type.
 * This logic is used consistently across the application for track identification.
 *
 * @param document - The SearchDocument to extract ID from
 * @returns The numeric track ID
 */
export const getTrackIdFromDocument = (
  document: SearchDocument | DocumentSchema
): number => {
  // For SFX, use sfx_variant_id as the unique identifier for playback
  if (document.asset_type === 'sfx' && document.sfx_variant_id) {
    return Number(document.sfx_variant_id);
  }

  // For music, use track_id if available
  if (document.track_id) {
    return Number(document.track_id);
  }

  // Fallback: extract numeric ID from the document ID string.
  // Reuses the canonical underscore-based parser so legacy ids of the form
  // "sunny-beats_123_premium" are handled correctly. With `id` now excluded
  // from engagement-collection responses, this path is rarely hit.
  return extractNumericId(document.id);
};

/**
 * Converts a SearchDocument from Typesense to a Track object for legacy component compatibility.
 *
 * ⚠️ **WARNING: Use with caution - this is a lossy conversion!**
 *
 * This function is designed **only** for passing data to legacy components that expect
 * the full Track interface (such as ConfirmDownloadModal). Many Track properties cannot
 * be inferred from SearchDocument and are filled with default values (null, undefined, 0, '').
 *
 * ## Limitations & Considerations:
 *
 * ### ❌ Missing/Default Values:
 * - `is_explicit_lyrics`: Always false (not available in SearchDocument)
 * - `current_engagement_group/current_engagement`: Always undefined
 * - `track_tempo`, `energy`, `intensity`: Always undefined
 * - Style IDs: Always 0 (only style names are available)
 * - Many metadata fields: Set to undefined or empty strings
 *
 * ### ✅ Reliable Mappings:
 * - Basic track info (id, name, slug)
 * - Premium status
 * - Asset type (music vs SFX)
 * - Contributor/artist information
 * - Track version data (length, preview URI, waveform)
 * - Tags and style names
 *
 * ## Recommended Usage:
 *
 * ```typescript
 * // ✅ Good: Legacy component compatibility
 * <ConfirmDownloadModal asset={{ type: 'music', data: searchDocumentToTrack(doc) }} />
 *
 * // ❌ Bad: Using for business logic that depends on accurate data
 * if (track.intensity > 5) { ... } // Will always be false!
 * if (track.current_engagement?.plays > 100) { ... } // Will throw!
 * ```
 *
 * ## Future Migration:
 * Legacy components should be refactored to accept minimal data objects containing
 * only the fields they actually need, rather than requiring the full Track interface.
 * This would eliminate the need for this lossy conversion function.
 *
 * @param document - The SearchDocument from Typesense to convert
 * @returns A Track object with many fields set to defaults due to data limitations
 *
 * @example
 * ```typescript
 * const searchResult = await typesenseClient.search(...);
 * const track = searchDocumentToTrack(searchResult.hits[0].document);
 *
 * // Safe to use for component props that only need basic info
 * console.log(track.name, track.is_premium); // ✅ Reliable
 *
 * // Dangerous to use for complex business logic
 * console.log(track.intensity); // ❌ Always undefined!
 * ```
 */
export const searchDocumentToTrack = (document: SearchDocument): Track => {
  // For SFX, use sfx_variant_id as the primary ID since it's the unique identifier
  // For music, use track_id as usual
  const primaryId =
    document.asset_type === 'sfx' && document.sfx_variant_id
      ? Number(document.sfx_variant_id)
      : document.track_id
        ? Number(document.track_id)
        : extractNumericId(document.id);

  const variantId =
    document.asset_type === 'sfx' && document.sfx_variant_id
      ? Number(document.sfx_variant_id)
      : document.track_version_id
        ? Number(document.track_version_id)
        : extractNumericId(document.id);

  return {
    id: primaryId,
    variant_id: variantId,
    name: document.name ?? 'Unknown Track',
    slug: document.track_slug ?? extractSlugFromDocumentId(document.id),
    current_context: { id: 0 },
    is_explicit_lyrics: false, // Not available in SearchDocument
    is_premium: document.is_premium,
    is_sfx: document.asset_type === 'sfx',
    artist: document.contributor_name
      ? {
          id: 0, // Not available in flat structure
          name: document.contributor_name ?? 'Unknown Contributor',
          slug: document.contributor_slug ?? '',
          avatar_image: document.contributor_image
            ? {
                image_aws_url: document.contributor_image,
              }
            : undefined,
        }
      : undefined,
    track_version: [
      {
        id: variantId, // Use the properly calculated variantId for both music and SFX
        version_length: document.version_length ?? 0,
        version_precise_length: document.version_precise_length ?? 0,
        version_preview_uri: document.version_preview_uri,
        waveform_json: undefined, // Waveform now fetched separately via useWaveform hook
      },
    ],
    track_tags: document.track_tags?.map((tag) => ({
      name: tag.name,
      slug: tag.slug,
    })),
    featured_track_tags: document.featured_tags?.map((tag) => ({
      name: tag.name,
      slug: tag.slug,
    })),
    track_to_style: document.styles?.map((style) => ({
      style: {
        id: 0, // Not available in SearchDocument
        name: style,
        slug: style.toLowerCase().replace(/\s+/g, '-'),
        url: '',
        track_tags: [],
        tile_image: '',
        order: 0,
        taxonomy: [],
      },
    })),
    // Optional fields with defaults
    current_engagement_group: undefined,
    current_engagement: undefined,
    description: undefined,
    schema_description: undefined,
    featured_sfx_tags: undefined,
    // The parent SFX id (distinct from the variant id in `id`/`variant_id`).
    // Required by the SFX download endpoint, which needs both ids.
    sfx_id: document.sfx_id ? Number(document.sfx_id) : undefined,
    intensity: undefined,
    is_premium_raw: document.is_premium ? 1 : 0,
    is_trending: document.is_trending,
    is_new: undefined,
    proximity: undefined,
    quantity: undefined,
    sfx_classification_id: undefined,
    sfx_tags: undefined,
    sfx_variant: undefined,
    variant_count: undefined,
    energy: undefined,
    track_tempo: undefined,
    duration: document.version_length,
    precise_duration: document.version_precise_length,
    // Trust-layer fields for the About-this-asset dialog (DEV-6280)
    is_exclusive: document.is_exclusive,
    set_live: document.set_live,
    written_by: document.written_by,
    musicvine_track_id: document.musicvine_track_id ?? undefined,
    cid_registration: undefined,
    dateCreated: undefined,
    play_order: undefined,
    needs_orfium_license: undefined,
    orfium_code: undefined,
    youtube_channel_id: undefined,
    youtube_channel_name: undefined,
    subscription_license_id: undefined,
    track_page_url: undefined,
    no_index: undefined,
  };
};
